/**
 * Well identity helpers — API / propnum / lease resolution + hash navigation.
 */

/** Digits-only API. Texas well APIs are often 10 (unique) or 14 (with sidetrack). */
export function normalizeApi(api) {
  if (api == null || api === '') return '';
  return String(api).replace(/\D/g, '');
}

/** Canonical 10-digit API for joins / notes when length ≥ 10. */
export function apiKey(api) {
  const digits = normalizeApi(api);
  if (digits.length >= 10) return digits.slice(0, 10);
  return digits;
}

/** Match 10-digit vs 14-digit (or dashed) APIs that refer to the same well. */
export function apisMatch(a, b) {
  const ka = apiKey(a);
  const kb = apiKey(b);
  if (!ka || !kb) return false;
  return ka === kb;
}

/** Prefer API (stable across map/master), then propnum, then lease. */
export function wellHashId(well) {
  if (!well) return '';
  const api = apiKey(well.api) || normalizeApi(well.api);
  if (api) return api;
  if (well.propnum) return String(well.propnum);
  if (well.lease) return String(well.lease);
  if (well.well) return String(well.well);
  return '';
}

export function navigateToWell(wellOrId) {
  let id =
    typeof wellOrId === 'string' || typeof wellOrId === 'number'
      ? String(wellOrId)
      : wellHashId(wellOrId);
  if (!id) return;
  try {
    id = decodeURIComponent(id);
  } catch {
    /* keep as-is */
  }
  window.location.hash = `well/${encodeURIComponent(id)}`;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function leaseKey(s) {
  return String(s || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

function safeDecode(value) {
  const raw = String(value || '').trim();
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Resolve a wellId (from #well/…) against DealPackage + optional map/geoscience rows.
 */
export function resolveWell(wellId, { dealPackage, mapWells = [], geoscienceFiles = [] } = {}) {
  const raw = safeDecode(wellId);
  if (!raw) return null;

  const assets = dealPackage?.assets || [];
  const streams = dealPackage?.streams || [];
  const apiNorm = normalizeApi(raw);
  const isPropnum = /^SALE\d+/i.test(raw);
  const lk = leaseKey(raw);

  let asset =
    (isPropnum && assets.find((a) => a.propnum === raw)) ||
    (apiNorm && assets.find((a) => apisMatch(a.api, apiNorm))) ||
    (lk && assets.find((a) => leaseKey(a.lease) === lk)) ||
    null;

  let mapWell =
    (apiNorm && mapWells.find((w) => apisMatch(w.api, apiNorm))) ||
    (asset && mapWells.find((w) => apisMatch(w.api, asset.api))) ||
    (lk && mapWells.find((w) => leaseKey(w.lease) === lk)) ||
    null;

  if (!asset && mapWell) {
    asset =
      assets.find((a) => apisMatch(a.api, mapWell.api)) ||
      assets.find((a) => leaseKey(a.lease) === leaseKey(mapWell.lease)) ||
      null;
  }

  const stream = asset
    ? streams.find((s) => s.propnum === asset.propnum) || null
    : null;

  const targetApi = apiKey(asset?.api || mapWell?.api || apiNorm);
  const leaseForLas = leaseKey(asset?.lease || mapWell?.lease || (apiNorm ? '' : raw));
  const lasFiles = (geoscienceFiles || []).filter((f) => {
    if (targetApi && apisMatch(f.api, targetApi)) return true;
    if (!leaseForLas) return false;
    const folder = leaseKey(f.wellFolder || f.wellName);
    return folder === leaseForLas;
  });

  const isBoswell =
    !!(asset?.isBoswell || /BOSWELL/i.test(asset?.lease || mapWell?.lease || raw));
  const boswellActuals = isBoswell ? dealPackage?.actuals?.boswell || null : null;
  const propnum = asset?.propnum || null;
  const fleetLife =
    (propnum && dealPackage?.actuals?.lifeOfWell?.byPropnum?.[propnum]) || null;
  // Prefer full Boswell series object; otherwise thin { lifeOfWell } from fleet map.
  const actuals = boswellActuals
    ? boswellActuals
    : fleetLife
      ? { propnum, lifeOfWell: fleetLife, stats: { firstProdDate: fleetLife.firstProdDate, cumGasMmcf: fleetLife.cumGasMmcf } }
      : null;

  if (!asset && !mapWell && !lasFiles.length && !actuals) {
    if (!lk && !apiNorm) return null;
  }

  const lasHint = lasFiles[0];
  const lease =
    asset?.lease ||
    mapWell?.lease ||
    lasHint?.wellName ||
    lasHint?.wellFolder ||
    raw;

  const canonicalApi =
    apiKey(asset?.api || mapWell?.api || lasHint?.api || apiNorm) ||
    asset?.api ||
    mapWell?.api ||
    lasHint?.api ||
    (apiNorm || null);

  return {
    wellId: raw,
    asset,
    mapWell,
    stream,
    actuals,
    lasFiles,
    identity: {
      propnum: asset?.propnum || null,
      api: canonicalApi,
      lease,
      status:
        mapWell?.status ||
        (asset?.resCat === '1PDP' ? 'PDP' : asset ? 'Modeled' : lasHint ? 'LAS only' : null),
      wellType: asset?.wellType || mapWell?.wellType || null,
      county: asset?.county || mapWell?.county || null,
      wi: asset?.wi ?? mapWell?.wi ?? null,
      nri: asset?.nri ?? mapWell?.nri ?? null,
      isBoswell: !!(asset?.isBoswell || /BOSWELL/i.test(lease || '')),
      lat: mapWell?.lat ?? null,
      lng: mapWell?.lng ?? null,
      modeled: asset != null || !!mapWell?.modeled,
    },
  };
}

/** Trailing production windows from monthly Mcfpd series (newest last). */
export function trailingProduction(months, key = 'mcfpd') {
  const list = (months || []).filter((m) => Number.isFinite(m[key]));
  if (!list.length) return null;
  const last = (n) => list.slice(-n);
  const avg = (arr) =>
    arr.length ? arr.reduce((s, m) => s + m[key], 0) / arr.length : null;
  const sumVol = (arr) =>
    arr.reduce((s, m) => s + (m.netMcf ?? m.grossMcf ?? m[key] * 30.4), 0);
  return {
    l3AvgMcfpd: avg(last(3)),
    l6AvgMcfpd: avg(last(6)),
    l12AvgMcfpd: avg(last(12)),
    l3VolMcf: sumVol(last(3)),
    l6VolMcf: sumVol(last(6)),
    l12VolMcf: sumVol(last(12)),
    monthsAvailable: list.length,
    firstMonth: list[0]?.month || list[0]?.date || null,
    lastMonth: list[list.length - 1]?.month || list[list.length - 1]?.date || null,
  };
}
